/**
 * FreeGiftPopup - Displays available free gifts when cart qualifies
 * Checks for products in FGWP collection and shows eligible gifts when cart total meets threshold
 * @element free-gift-popup
 */
if (!customElements.get('free-gift-popup')) {
  customElements.define('free-gift-popup', class FreeGiftPopup extends HTMLElement {
    constructor() {
      super();

      // Component state
      this.cartUpdateUnsubscriber = undefined;
      this.eligibleGifts = []; // Gifts that meet the cart total threshold
      this.qualifiedGifts = []; // All potential gift products from the source
      this.giftProductIds = new Set(); // Product IDs of all potential gift products
      this.cartTotal = 0;
      this.isOpen = false;
      this.addedGiftIds = []; // Track variant IDs of free gifts currently in the cart
      this.currentGiftUsage = 0; // Tracks the total number of free gifts added in this session/interaction

      // CSS variables
      this.style.setProperty('--fgwp-transition-duration', '0.3s');
      this.style.setProperty('--fgwp-z-index', '100');
    }

    /**
     * Initialize component when connected to DOM
     */
    connectedCallback() {
      this.closeButton = this.querySelector('.free-gift-popup__close');
      this.overlay = this.querySelector('.free-gift-popup__overlay');
      this.contentContainer = this.querySelector('.free-gift-popup__content');

      // Add close button event listener
      if (this.closeButton) {
        this.closeButton.addEventListener('click', function handleCloseButtonClick() {
          this.close();
        }.bind(this));
      }

      // Add overlay click event listener
      if (this.overlay) {
        this.overlay.addEventListener('click', function handleOverlayClick() {
          this.close();
        }.bind(this));
      }

      // Add escape key event listener
      document.addEventListener('keyup', function handleKeyUp(evt) {
        if (evt.code === 'Escape' && this.isOpen) this.close();
      }.bind(this));

      // Subscribe to cart update events
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, function handleCartUpdate(event) {
        // Fetch cart total and check for gifts on every cart update
        this.getCartTotal()
          .then(function processCartTotal(cartTotal) {
            this.cartTotal = cartTotal;
            this.checkForEligibleGifts(); // Check for gifts after getting the latest cart total
          }.bind(this))
          .catch(function handleError(error) {
            console.error('Error getting cart total:', error);
          });
      }.bind(this));

      // Initial check when component loads
      // this.getCartTotal()
      //   .then(function processInitialCartTotal(cartTotal) {
      //     this.cartTotal = cartTotal;
      //     this.checkForEligibleGifts();
      //   }.bind(this))
      //   .catch(function handleInitialError(error) {
      //     console.error('Error getting initial cart total:', error);
      //   });
    }

    /**
     * Clean up event listeners when disconnected from DOM
     */
    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }

      if (this.closeButton) {
        this.closeButton.removeEventListener('click', this.close);
      }

      if (this.overlay) {
        this.overlay.removeEventListener('click', this.close);
      }

      document.removeEventListener('keyup', this.handleKeyUp);
    }

    /**
     * Calculate total cart value excluding free gifts by fetching current cart data
     * Also counts the total free gifts already in the cart
     * @returns {Promise<number>} - Promise resolving to cart total in cents
     */
    async getCartTotal() {
      try {
        await this.fetchEligibleGifts();
        // Fetch current cart data directly from cart.js endpoint
        const response = await fetch(`${window.routes.cart_url}.js`);
        if (!response.ok) {
          throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
        }

        const cartData = await response.json();
        let calculatedCartTotal = 0; // Use a different variable name to avoid conflict with this.cartTotal

        // Reset gift tracking info for current state calculation
        this.addedGiftIds = [];
        this.currentGiftUsage = 0; // Reset usage based on current cart state

        // Calculate cart total excluding any items that are already free gifts
        // And track total free gift usage based on items currently in the cart
        if (cartData.items) {
          cartData.items.forEach(function filterFreeGifts(item) {
            // console.log('Cart item:', item);
            // Also check if this product ID is in our gift products set (for items added from product page)
            // console.log('gift product ids', this.giftProductIds);
            // console.log('item product id', item.product_id);
            const isGiftProductId = this.giftProductIds.has(Number(item.product_id));

            if (isGiftProductId) {
              // Track free gift IDs and increment usage count based on cart contents
              this.addedGiftIds.push(item.variant_id);
              this.currentGiftUsage += item.quantity; // Count based on quantity
              console.log(`Found gift in cart: ${item.product_id} (${item.title}), isGiftProductId: ${isGiftProductId}`);
            } else {
              // Add the price of non-free items to the total
              calculatedCartTotal += item.line_price;
            }
          }.bind(this));
        }

        return calculatedCartTotal; // Return the calculated value
      } catch (error) {
        console.error('Error fetching cart data:', error);
        return 0; // Return 0 as fallback
      }
    }

    /**
     * Calculate how many gifts a customer qualifies for based on their cart total and the lowest threshold.
     * @returns {number} - Number of gifts they qualify for in total.
     */
    calculateGiftsQualifiedFor() {
      // Use all potential gifts (this.qualifiedGifts) fetched initially to find the *lowest* threshold
      const validThresholds = this.qualifiedGifts
        .map(function getThreshold(gift) { return gift.fgwpThreshold || 0; })
        .filter(function filterValidThresholds(threshold) { return threshold > 0; });

      if (validThresholds.length === 0) {
        return 0; // No gifts if no thresholds are set
      }

      const lowestThreshold = Math.min(...validThresholds);

      // If no valid lowest threshold found or cart total is zero or less
      if (!isFinite(lowestThreshold) || lowestThreshold <= 0 || this.cartTotal <= 0) {
        return 0;
      }

      // CHANGE THIS LINE: Always return 1 if they meet the threshold, regardless of cart value
      return this.cartTotal >= lowestThreshold ? 1 : 0;

      // Original logic (commented out):
      // const qualifiedCount = Math.floor(this.cartTotal / lowestThreshold);
      // return qualifiedCount;
    }

    /**
     * Check for products eligible as free gifts based on cart total
     */
    checkForEligibleGifts() {
      // Fetch all potential gifts first
      this.fetchEligibleGifts()
        .then(function storeQualifiedGifts(gifts) {
          this.qualifiedGifts = gifts; // Store all potential gifts

          // Now filter based on current cart total to find currently eligible gifts
          this.eligibleGifts = this.qualifiedGifts.filter(function filterByThreshold(gift) {
            return this.cartTotal >= (gift.fgwpThreshold || 0);
          }.bind(this));

          // Calculate how many gifts they qualify for based on the lowest threshold overall
          const giftsQualifiedFor = this.calculateGiftsQualifiedFor();
          const giftsAlreadyInCart = this.currentGiftUsage; // Use the count from getCartTotal

          // Show popup only if:
          // 1. There are gifts they *could* choose from (eligibleGifts)
          // 2. They qualify for more gifts than they already have in the cart
          if (this.eligibleGifts.length > 0 && giftsQualifiedFor > giftsAlreadyInCart) {
            this.renderGiftOptions();
            this.open();
          } else {
            if (this.isOpen) {
              this.close(); // Close if open and they no longer qualify
            }
          }
          console.log(`Eligible gifts: ${this.eligibleGifts.length}, Qualified gifts: ${giftsQualifiedFor}, Already in cart: ${giftsAlreadyInCart}`);
        }.bind(this))
        .catch(function handleError(error) {
          console.error('Error fetching or processing eligible gifts:', error);
        });
    }

    /**
     * Fetch products eligible as free gifts from the embedded data
     * @returns {Promise<Array>} - Promise resolving to array of eligible gift products
     */
    async fetchEligibleGifts() {
      // Get product information from embedded data in the template
      const fgwpProductsContainer = this.querySelector('.fgwp-products');

      // If container exists, parse and return the pre-rendered product data
      if (fgwpProductsContainer && fgwpProductsContainer.textContent) {
        try {
          const fgwpProducts = JSON.parse(fgwpProductsContainer.textContent || '[]');

          // Update our set of gift product IDs
          this.giftProductIds.clear();
          fgwpProducts.forEach(function storeGiftProductId(gift) {
            if (gift.id) {
              this.giftProductIds.add(gift.id);
            }
          }.bind(this));

          console.log(`Updated gift product IDs set with ${this.giftProductIds.size} products`);
          return Promise.resolve(fgwpProducts);
        } catch (error) {
          console.error('Error parsing free gift products data:', error);
          return Promise.resolve([]);
        }
      }

      // If we don't have embedded product data, return empty array
      console.warn('Free gift products container not found or empty. Check the Liquid template.');
      return Promise.resolve([]);
    }

    /**
     * Render gift options in the popup content
     */
    renderGiftOptions() {
      if (!this.contentContainer) return;

      // Clear existing content
      this.contentContainer.innerHTML = '';

      const giftsQualifiedFor = this.calculateGiftsQualifiedFor();
      const giftsAlreadyInCart = this.currentGiftUsage; // Get count from cart analysis
      const remainingGifts = Math.max(0, giftsQualifiedFor - giftsAlreadyInCart); // Ensure non-negative

      // Determine the lowest threshold among all qualified gifts for messaging
      const validThresholds = this.qualifiedGifts
        .map(function getThreshold(gift) { return gift.fgwpThreshold || 0; })
        .filter(function filterValidThresholds(threshold) { return threshold > 0; });
      const lowestThreshold = validThresholds.length > 0 ? Math.min(...validThresholds) : 0;
      const thresholdFormatted = lowestThreshold > 0 ? (lowestThreshold / 100).toFixed(2) : 'N/A';

      // Add heading
      const heading = document.createElement('h2');
      heading.classList.add('free-gift-popup__heading');
      heading.textContent = 'Choose Your Free Gift!';
      this.contentContainer.appendChild(heading);

      // Create description
      const description = document.createElement('p');
      description.classList.add('free-gift-popup__description');
      description.textContent = 'Select a free gift to add to your cart.';
      this.contentContainer.appendChild(description);

      // Create gift options container
      const giftContainer = document.createElement('div');
      giftContainer.classList.add('free-gift-popup__options');

      this.eligibleGifts.forEach(function createGiftOption(gift) {
        const giftOption = document.createElement('div');
        giftOption.classList.add('free-gift-popup__option');

        // Create gift image
        const giftImage = document.createElement('img');
        giftImage.classList.add('free-gift-popup__option-image');
        giftImage.src = gift.featured_image ?? '';
        giftImage.alt = gift.title;

        // Create gift title
        const giftTitle = document.createElement('h3');
        giftTitle.classList.add('free-gift-popup__option-title');
        giftTitle.textContent = gift.title;

        // Create action button
        const actionButton = document.createElement('button');
        actionButton.classList.add('free-gift-popup__option-button', 'button', 'button--primary');
        actionButton.setAttribute('data-product-id', gift.id);

        // Check if product has multiple variants
        const hasMultipleVariants = gift.variants && gift.variants.length > 1;

        // Check if the user can add more gifts
        const canAddMoreGifts = remainingGifts > 0;

        if (!canAddMoreGifts) {
          // User has already added the maximum number of gifts they qualify for
          actionButton.textContent = 'Limit Reached';
          actionButton.disabled = true;
          actionButton.classList.add('button--disabled');
          giftOption.classList.add('free-gift-popup__option--disabled');
        } else if (hasMultipleVariants) {
          // For products with multiple variants, redirect to product page
          actionButton.textContent = window.variantStrings.chooseOption || 'Choose Option';
          actionButton.addEventListener('click', function handleRedirectToProduct() {
            this.close();
            const productUrl = `/products/${gift.handle}?free_gift=true&threshold=${gift.fgwpThreshold || 0}`;
            window.location.href = productUrl;
          }.bind(this));
        } else {
          // For products with single variant, add to cart directly
          actionButton.textContent = window.variantStrings.addToCart || 'Add to Cart';
          actionButton.addEventListener('click', function handleAddGiftClick() {
            this.addGiftToCart(gift);
          }.bind(this));
        }

        // Add elements to gift option
        giftOption.appendChild(giftImage);
        giftOption.appendChild(giftTitle);
        giftOption.appendChild(actionButton); // Fixed: append actionButton to giftOption, not to itself

        // Add gift option to container
        giftContainer.appendChild(giftOption);
      }.bind(this));

      this.contentContainer.appendChild(giftContainer);

      // Create no thanks button
      const noThanksButton = document.createElement('button');
      noThanksButton.classList.add('free-gift-popup__no-thanks', 'button', 'button--tertiary');
      noThanksButton.textContent = 'No thanks';
      noThanksButton.addEventListener('click', function handleNoThanksClick() {
        this.close();
      }.bind(this));

      this.contentContainer.appendChild(noThanksButton);
    }

    /**
     * Add selected gift to cart
     * @param {Object} gift - Gift product to add to cart
     */
    addGiftToCart(gift) {
      // Use first variant or the gift's first variant ID
      const variantId = gift.variants && gift.variants[0] ? gift.variants[0].id : gift.id;

      // Recalculate qualification status before adding
      const giftsQualifiedFor = this.calculateGiftsQualifiedFor();
      const giftsAlreadyInCart = this.currentGiftUsage;

      // Check if they've reached their gift limit based on qualification
      if (giftsAlreadyInCart >= giftsQualifiedFor) {
        console.log(`Free gift limit reached based on qualification: ${giftsAlreadyInCart}/${giftsQualifiedFor}`);
        this.close();
        alert('You have already added the maximum number of free gifts you qualify for.');
        return;
      }

      // Create FormData instance
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      // Store the gift product ID to ensure we recognize this item in the cart
      if (gift.id) {
        this.giftProductIds.add(gift.id);
      }

      // Add sections for cart rendering if cart component exists
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map(function mapSection(section) {
            return section.id;
          })
        );
        formData.append('sections_url', window.location.pathname);
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      // Show loading state
      this.contentContainer.classList.add('loading');

      fetch(`${window.routes.cart_add_url}`, config)
        .then(function handleResponse(response) {
          return response.json();
        })
        .then(function processResponse(data) {
          if (data.status && data.status !== 'success') {
            throw new Error(data.message || 'Error adding gift to cart');
          }

          // Publish cart update event
          if (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'free-gift-popup',
              productVariantId: variantId,
              cartData: data
            });
          }
        }.bind(this))
        .catch(function handleError(error) {
          console.error('Error adding gift to cart:', error);
        })
        .finally(function finishLoading() {
          this.contentContainer.classList.remove('loading');
          this.close();
        }.bind(this));
    }

    /**
     * Open the free gift popup
     */
    open() {
      if (this.isOpen) return;

      // Close cart drawer if it exists to avoid both being open simultaneously
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.close === 'function') {
        cartDrawer.close();
      }

      this.setAttribute('open', '');
      this.isOpen = true;
      document.body.classList.add('overflow-hidden');

      setTimeout(function animateOpen() {
        this.classList.add('animate', 'active');
      }.bind(this), 10);

      // Trap focus within the popup
      this.addEventListener('transitionend', function trapFocusOnOpen() {
        trapFocus(this);
        this.removeEventListener('transitionend', trapFocusOnOpen);
      }.bind(this), { once: true });
    }

    /**
     * Close the free gift popup
     */
    close() {
      if (!this.isOpen) return;

      this.classList.remove('active');
      this.isOpen = false;
      document.body.classList.remove('overflow-hidden');

      // Remove attribute after animation completes
      this.addEventListener('transitionend', function removeOpenAttribute() {
        this.removeAttribute('open');
        removeTrapFocus();
        this.removeEventListener('transitionend', removeOpenAttribute);
      }.bind(this), { once: true });
    }
  });
};
